import type { AnswerRecord, CourseResult, CourseStep } from './course.js'

/**
 * Bodování kurzu. Čistá funkce bez Reactu, bez DOM, bez času —
 * uplynulé sekundy se předávají zvenčí, aby šlo vše deterministicky testovat.
 *
 * Pravidla jsou převzatá 1:1 z původní aplikace VČETNĚ jejich zvláštností.
 * Nesahat bez přečtení skillu `safetutor-contract`.
 */

export interface ScoringState {
  totalPoints: number
  totalMaxPoints: number
  answers: readonly AnswerRecord[]
}

export function initialScoring(): ScoringState {
  return { totalPoints: 0, totalMaxPoints: 0, answers: [] }
}

export interface AnswerAttempt {
  step: CourseStep
  /** Kolikátý pokus na tuhle otázku, 1-based. */
  attempt: number
  wasCorrect: boolean
  /** 1-based index zvolené odpovědi nebo hotspotu. */
  selectedAnswer: number
  /** Sekundy strávené na otázce. */
  elapsedSec: number
}

/**
 * Body za jeden pokus.
 *
 * Zvláštnosti převzaté z původní aplikace:
 *  - časová penalizace se odečítá i u špatné odpovědi, ale výsledek se pak
 *    stejně vynuluje — pořadí operací proto zachováno
 *  - záporný výsledek se ořízne na nulu
 */
export function pointsForAttempt(a: AnswerAttempt): number {
  let points: number
  switch (a.attempt) {
    case 1:
      points = a.step.pointsMax
      break
    case 2:
      points = a.step.pointsSecondAttempt
      break
    default:
      points = 0
      break
  }

  if (a.elapsedSec > a.step.timeLimitSec) points -= a.step.pointsTimePenalty

  if (!a.wasCorrect) points = 0
  if (points < 0) points = 0

  return points
}

/**
 * Zaznamená pokus. Vrací nový stav, původní nemění.
 *
 * `totalMaxPoints` roste POUZE při správné odpovědi. Vypadá to jako chyba,
 * ale je to současné produkční chování a mění výsledky zákazníků — nechat tak.
 */
export function recordAnswer(state: ScoringState, a: AnswerAttempt): ScoringState {
  const record: AnswerRecord = {
    legacyIndex: a.step.legacyIndex,
    selectedAnswer: a.selectedAnswer,
    wasCorrect: a.wasCorrect,
    timeSec: a.elapsedSec,
  }

  return {
    totalPoints: state.totalPoints + pointsForAttempt(a),
    totalMaxPoints: state.totalMaxPoints + (a.wasCorrect ? a.step.pointsMax : 0),
    answers: [...state.answers, record],
  }
}

/**
 * Uzavře kurz.
 *
 * Při `totalMaxPoints === 0` vyjde `0/0 = NaN` a `NaN >= x` je v JS false,
 * takže `passed` je false. Původní aplikace se chovala stejně — replikujeme,
 * neošetřujeme "hezčeji".
 */
export function finalize(state: ScoringState, pointsToPassPercent: number): CourseResult {
  const percent = (state.totalPoints / state.totalMaxPoints) * 100

  return {
    totalMaxPoints: state.totalMaxPoints,
    totalPoints: state.totalPoints,
    pointsToPassPercent,
    passed: percent >= pointsToPassPercent,
    answers: [...state.answers],
  }
}
